import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DateTimePicker } from '@mui/x-date-pickers';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Save01, Trash01 } from 'untitledui-js-base';
import { StatementRequest } from '../types/statement';

const STATEMENT_TYPES = {
  CUSTOM: 'custom',
  VACATION: 'vacation',
  PARAGRAPH: 'paragraph',
  SICKDAY: 'sickday'
};

interface AddNewStatementDialogProps extends Omit<DialogProps, 'onSubmit'> {
  open: boolean;
  defaultData?: StatementRequest;
  dealerId: number;
  onSubmit: (data: StatementRequest) => void;
  onUpdate?: (data: StatementRequest) => void;
  onRemove?: () => void;
  isLoading?: boolean;
}

export const AddNewStatementDialog = ({
  open,
  defaultData,
  onClose,
  dealerId,
  onSubmit,
  onUpdate,
  onRemove,
  isLoading,
  ...props
}: AddNewStatementDialogProps) => {
  const methods = useForm<StatementRequest>({
    defaultValues: {
      datetime: new Date(),
      dealerId,
      note: '',
      type: STATEMENT_TYPES.CUSTOM
    }
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    reset
  } = methods;

  useEffect(() => {
    if (open) {
      reset({
        datetime: defaultData?.datetime ?? new Date(),
        dealerId: defaultData?.dealerId ?? dealerId,
        note: defaultData?.note ?? '',
        privateKm: defaultData?.privateKm ?? 0,
        type: defaultData?.type ?? STATEMENT_TYPES.CUSTOM
      });
    }
  }, [open, defaultData]);

  const handleSubmitForm = (data: StatementRequest) => {
    if (defaultData && !isEmpty(defaultData)) {
      onUpdate?.(data);
    } else {
      onSubmit(data);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="xs" {...props}>
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <input type="hidden" {...register('dealerId')} />
        <DialogTitle id="alert-dialog-title">{defaultData ? 'Úprava záznamu' : 'Přidání nového výkazu'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={2}>
            <Grid size={12}>
              <Controller
                control={control}
                name="datetime"
                render={({ field: { onChange, value } }) => (
                  <DateTimePicker
                    value={value}
                    onChange={onChange}
                    format="dd.MM.yyyy HH:mm"
                    sx={{ width: 1 }}
                    slotProps={{
                      textField: {
                        error: Boolean(errors.datetime),
                        helperText: errors.datetime?.message
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel
                  id="type-label"
                  sx={{
                    backgroundColor: 'white',
                    px: 0.5
                  }}
                >
                  Typ záznamu
                </InputLabel>
                <Controller
                  control={control}
                  name="type"
                  render={({ field: { onChange, value } }) => (
                    <Select value={value} onChange={onChange} labelId="type-label">
                      <MenuItem value={STATEMENT_TYPES.CUSTOM}>Vlastní záznam</MenuItem>
                      <MenuItem value={STATEMENT_TYPES.VACATION}>Dovolená</MenuItem>
                      <MenuItem value={STATEMENT_TYPES.PARAGRAPH}>Paragraf</MenuItem>
                      <MenuItem value={STATEMENT_TYPES.SICKDAY}>SickDay</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="privateKm"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    type="number"
                    value={value}
                    onChange={(event) => onChange(Number.parseFloat(event.target.value))}
                    label="Privátní kilometry"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.privateKm)}
                    helperText={errors.privateKm?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="note"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Poznámka"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.note)}
                    helperText={errors.note?.message}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    minRows={3}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            py: 4,
            justifyContent: 'center',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            gap: 2
          }}
        >
          <Button onClick={(event) => onClose?.(event, 'backdropClick')}>Zavřít</Button>
          <Button
            type="submit"
            color="success"
            variant="contained"
            startIcon={<Save01 />}
            size="large"
            loading={isLoading}
          >
            {defaultData ? 'Upravit záznam' : 'Založit záznam'}
          </Button>
          {defaultData && (
            <IconButton color="error" onClick={onRemove}>
              <Trash01 />
            </IconButton>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
